import {cleanResearchText, cleanResearchSources} from './publicResearchText';
export const INCOMPLETE_PEP_RESEARCH_NOTICE = 'Incomplete research · Some public-web findings could not be verified. Re-run screening';
export function hasIncompletePepResearch(payload) {
  return payload?.researchIncomplete === true || (payload?.items || []).some(item => item?.result?.researchStatus === 'partial');
}
// Google-grounded final text and links are already validated by the API.
// Preserve them when expanding saved results; React renders text, not HTML.
function googleResearchSources(sources) {
  return (Array.isArray(sources)?sources:[]).flatMap(source=>{
    const url=typeof source==='string'?source:source?.url;
    try {
      if(!['http:','https:'].includes(new URL(url).protocol))return [];
      return [{url,title:typeof source==='string'?'':String(source.title||source.name||'')}];
    } catch{return [];}
  });
}
// One saved research item per screened subject; each candidate has its own evidence key.
export function pepWebCandidates(item) {
  if (!item?.result) return [];
  const multi = Array.isArray(item.result.candidates);
  const results = multi ? item.result.candidates : [item.result];
  return results.filter(result => ['pep','pep_related','close_associate'].includes(result?.category)).map((result, index) => {
    const grounding=result.grounding||item.result.grounding||item.grounding;
    const google=grounding?.provider==='google_search';
    return {
    ...item, grounding,
    result: {...result, assessment: google?String(result.assessment||''):cleanResearchText(result.assessment), sources:google?googleResearchSources(result.sources):cleanResearchSources(result.sources)},
    evidenceKey: multi ? `ai|${item.subjectKey}|${result.candidateId || `candidate_${index}`}` : `ai|${item.subjectKey}`,
  };});
}
export function pepWebClassification(category) {
  return category === 'pep' ? 'PEP' : ['pep_related','close_associate'].includes(category) ? 'Close to PEP' : '';
}

// Keep failed research distinct from a source genuinely omitting a field.
export function applyWebResearchStatus(context, result) {
  const fields = {...context.fields};
  for (const key of ['dob', 'nationality', 'domicile']) {
    if (result.contextResearch?.[key] === 'research_failed' && !result.identityEvidence?.[key]?.length && fields[key]) {
      fields[key] = {...fields[key], status: 'unavailable', label: 'Unavailable',
        explanation: 'This identity field could not be verified during research. Re-run screening to try again.'};
    }
  }
  if (result.contextCountryField === 'residence' && fields.domicile) {
    fields.domicile = {...fields.domicile, displayName: 'Residence'};
  }
  return {...context, fields};
}

export function pepWebIdentityLine(result) {
  const present = value => value && !/^(?:n\/?a|unknown|none)$/i.test(String(value).trim());
  const residenceLabel = result.residence_status === 'historical' ? 'Former residence' : result.residence_status === 'unknown' ? 'Residence (date unknown)' : 'Residence';
  const parts = [['Nationality', result.matchedProfileNationality], ['DOB', result.matchedProfileDob], [residenceLabel, result.country_of_residence], ['Domicile', result.matchedProfileDomicile]]
    .filter(([,value]) => present(value)).map(([label,value]) => `${label}: ${cleanResearchText(value)}`);
  if (['pep_related','close_associate'].includes(result.category) && present(result.relatedPepName)) parts.push(`Associated PEP: ${cleanResearchText(result.relatedPepName)}`);
  return parts.join(' · ');
}

const sameSubjectName = (a,b) => String(a||'').normalize('NFKC').trim().replace(/\s+/g,' ').toLowerCase() === String(b||'').normalize('NFKC').trim().replace(/\s+/g,' ').toLowerCase();
export function previousPepFindings(items, subject) {
  return (items||[]).filter(item=>item.subjectKey===subject.key && sameSubjectName(item.subjectName,subject.name))
    .flatMap(pepWebCandidates).filter(item=>item.result.sources.length).slice(0,12)
    .map(({result})=>({matchedProfileName:result.matchedProfileName,matchedProfileRole:result.matchedProfileRole,
      relatedPepName:result.relatedPepName,sources:result.sources}));
}
export function previousPepResearch(payload, subjects) {
  const items=payload?.previousItems?.length ? payload.previousItems : payload?.items || [];
  return items.filter(item=>subjects.some(s=>s.key===item.subjectKey && sameSubjectName(item.subjectName,s.name)) && pepWebCandidates(item).length)
    .map(item=>({...item,previousResearch:true,researchedAt:item.researchedAt || item.result?.researchedAt || payload.updatedAt || null}));
}
export function previousPepResearchLabel(item) {
  if(!item?.previousResearch)return '';
  const date=new Date(item.researchedAt || '');
  const dated=Number.isNaN(date.getTime())?'':` (${date.toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})})`;
  return `Previous research${dated} · Refresh unavailable`;
}
